import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { TID } from "../lib/testIds";
import { Lock, ShieldCheck, ShieldAlert, Ban, HelpCircle, AlertTriangle } from "lucide-react";

const BLOCKED_DOMAINS = [
  "google.com", "gmail.com", "youtube.com", "gemini.google.com",
  "microsoft.com", "microsoftonline.com", "live.com", "outlook.com", "office.com",
  "facebook.com", "instagram.com", "meta.com", "whatsapp.com",
  "apple.com", "icloud.com",
];

function isBlockedUrl(url) {
  if (!url) return null;
  try {
    const h = new URL(url).hostname.toLowerCase();
    const hit = BLOCKED_DOMAINS.find((d) => h === d || h.endsWith("." + d));
    return hit || null;
  } catch { return null; }
}

// Whitelist mirrored from backend + steps-interpreter.js. Client-side check
// is only UX assistance; the KRAI API is the authoritative validator.
const ALLOWED_STEP_OPS = ["goto", "type", "click", "check", "select", "waitFor", "waitTimeout", "press"];
const MAX_STEPS_CLIENT = 40;

const DEFAULT_STEPS_EXAMPLE = JSON.stringify([
  { op: "goto",    url: "https://ejemplo.com/login" },
  { op: "click",   selector: "#accept-cookies" },
  { op: "type",    selector: "#email",    value: "$user" },
  { op: "type",    selector: "#password", value: "$password" },
  { op: "click",   selector: "button[type=submit]" },
  { op: "waitFor", selector: ".dashboard" }
], null, 2);

function validateStepsClient(text) {
  let parsed;
  try { parsed = JSON.parse(text); }
  catch (e) { return { error: "JSON inválido: " + e.message }; }
  if (!Array.isArray(parsed) || parsed.length === 0) {
    return { error: "steps debe ser un array no vacío" };
  }
  if (parsed.length > MAX_STEPS_CLIENT) {
    return { error: `steps supera el máximo (${MAX_STEPS_CLIENT})` };
  }
  for (let i = 0; i < parsed.length; i++) {
    const s = parsed[i];
    if (!s || typeof s !== "object" || Array.isArray(s)) {
      return { error: `step[${i}] debe ser un objeto` };
    }
    if (!ALLOWED_STEP_OPS.includes(s.op)) {
      return { error: `step[${i}]: op no permitida "${s.op}"` };
    }
  }
  return { steps: parsed };
}

const METHODS = [
  { value: "template", label: "Plantilla (selectores)" },
  { value: "steps",    label: "Steps DSL (JSON declarativo)" },
];

const EMPTY = {
  name: "",
  url: "",
  login_url: "",
  logged_in_selector: "",
  method: "template",
  user_selector: "",
  password_selector: "",
  submit_selector: "",
  // Optional template extensions (all backwards-compatible)
  pre_click_selector: "",
  success_url_pattern: "",
  iframe_selector: "",
  mfa_wait_selector: "",
  steps_text: DEFAULT_STEPS_EXAMPLE,
  user: "",
  password: "",
};

export default function AddSessionDialog({
  open,
  onOpenChange,
  editing = null,
  onSaved,
}) {
  const isEdit = Boolean(editing);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showAdvancedTpl, setShowAdvancedTpl] = useState(false);

  // A session is considered "legacy script" when it exists with method:"script"
  // (uploads have been deprecated → the UI shows read-only info + a way to
  // migrate it to steps/template but never lets it stay in script mode).
  const editingLegacyScript = Boolean(isEdit && editing && editing.method === "script");

  const blockedDomain = useMemo(
    () => isBlockedUrl(form.url) || isBlockedUrl(form.login_url),
    [form.url, form.login_url]
  );

  useEffect(() => {
    if (!open) return;
    if (isEdit && editing) {
      const legacyScript = editing.method === "script";
      setForm({
        name: editing.name || "",
        url: editing.url || "",
        login_url: editing.login_url || "",
        logged_in_selector: editing.logged_in_selector || "",
        // A legacy script session opens as "template" so the user can migrate
        // it forward. The script itself stays on disk (never auto-deleted).
        method: legacyScript ? "template" : (editing.method || "template"),
        user_selector: editing.template?.user_selector || "",
        password_selector: editing.template?.password_selector || "",
        submit_selector: editing.template?.submit_selector || "",
        pre_click_selector: editing.template?.pre_click_selector || "",
        success_url_pattern: editing.template?.success_url_pattern || "",
        iframe_selector: editing.template?.iframe_selector || "",
        mfa_wait_selector: editing.template?.mfa_wait_selector || "",
        steps_text: Array.isArray(editing.steps) && editing.steps.length
          ? JSON.stringify(editing.steps, null, 2)
          : DEFAULT_STEPS_EXAMPLE,
        user: "",
        password: "",
      });
      setShowAdvancedTpl(Boolean(
        editing.template?.pre_click_selector ||
        editing.template?.success_url_pattern ||
        editing.template?.iframe_selector ||
        editing.template?.mfa_wait_selector
      ));
    } else {
      setForm(EMPTY);
      setShowAdvancedTpl(false);
    }
  }, [open, isEdit, editing]);

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e) {
    e.preventDefault();
    if (blockedDomain) {
      toast.error(
        `${blockedDomain} bloquea logins automatizados. Usa el módulo Navegador / Playwright.`
      );
      return;
    }
    if (!form.name.trim() || !form.url.trim() || !form.logged_in_selector.trim()) {
      toast.error("Nombre, URL y selector de sesión activa son obligatorios");
      return;
    }
    if (!isEdit && (!form.user.trim() || !form.password)) {
      toast.error("Usuario y contraseña obligatorios al crear");
      return;
    }
    if (form.method === "template") {
      if (!form.user_selector || !form.password_selector || !form.submit_selector) {
        toast.error("Selectores user/password/submit obligatorios en modo plantilla");
        return;
      }
    }
    let stepsValidated = null;
    if (form.method === "steps") {
      const v = validateStepsClient(form.steps_text);
      if (v.error) { toast.error(v.error); return; }
      stepsValidated = v.steps;
    }
    setSaving(true);
    try {
      let saved;
      const templatePayload = form.method === "template" ? {
        user_selector: form.user_selector,
        password_selector: form.password_selector,
        submit_selector: form.submit_selector,
        ...(form.pre_click_selector   ? { pre_click_selector:   form.pre_click_selector }   : {}),
        ...(form.success_url_pattern  ? { success_url_pattern:  form.success_url_pattern }  : {}),
        ...(form.iframe_selector      ? { iframe_selector:      form.iframe_selector }      : {}),
        ...(form.mfa_wait_selector    ? { mfa_wait_selector:    form.mfa_wait_selector }    : {}),
      } : null;

      if (isEdit) {
        const patch = {
          url: form.url,
          login_url: form.login_url,
          logged_in_selector: form.logged_in_selector,
          method: form.method,
        };
        if (templatePayload) patch.template = templatePayload;
        if (stepsValidated)  patch.steps    = stepsValidated;
        if (form.user.trim()) patch.user = form.user.trim();
        if (form.password) patch.password = form.password;
        const { data } = await api.patch(
          `/sessions/${encodeURIComponent(editing.name)}`,
          patch
        );
        saved = data;
      } else {
        const body = {
          name: form.name.trim(),
          url: form.url,
          login_url: form.login_url,
          logged_in_selector: form.logged_in_selector,
          method: form.method,
          user: form.user.trim(),
          password: form.password,
        };
        if (templatePayload) body.template = templatePayload;
        if (stepsValidated)  body.steps    = stepsValidated;
        const { data } = await api.post("/sessions", body);
        saved = data;
      }

      toast.success(isEdit ? "Sesión actualizada" : "Sesión creada");
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={TID.sessions.dialog}
        className="bg-zinc-950 border border-zinc-800 text-zinc-100 sm:max-w-[640px] max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Lock className="h-4 w-4" />
            {isEdit ? `Editar sesión — ${editing.name}` : "Nueva sesión"}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Las credenciales se cifran con AES-256-GCM en KRAI API. KRAI CONNECT
            nunca las almacena ni las devuelve al navegador.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {/* Help */}
          <div className="rounded-md border border-zinc-800 bg-black/30">
            <button
              type="button"
              onClick={() => setShowHelp((v) => !v)}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-zinc-300 hover:text-white"
            >
              <HelpCircle className="h-3.5 w-3.5 text-zinc-400" />
              ¿Qué es "plantilla" y qué es "selector"? {showHelp ? "▾" : "▸"}
            </button>
            {showHelp && (
              <div className="px-4 pb-4 pt-1 text-[12px] text-zinc-400 leading-relaxed space-y-2">
                <p>
                  <b className="text-zinc-200">Plantilla</b>: le decimos al navegador
                  <b> dónde escribir</b> el usuario y la contraseña, y <b>qué botón pulsar</b>.
                  Para eso necesita "selectores CSS", que son las coordenadas técnicas de cada campo.
                </p>
                <p>
                  <b className="text-zinc-200">Selector CSS</b>: no es tu email ni tu contraseña.
                  Es algo como <code className="font-mono text-zinc-300">input[type=email]</code> o
                  <code className="font-mono text-zinc-300">#password</code>. Tus datos reales van
                  <b> solo</b> en la caja verde de abajo ("Usuario / email" y "Contraseña").
                </p>
                <p>
                  <b className="text-zinc-200">Cómo obtener un selector</b>: en el navegador, botón
                  derecho sobre el campo → "Inspeccionar" → click derecho en el HTML resaltado →
                  "Copy" → "Copy selector".
                </p>
                <p>
                  <b className="text-zinc-200">Script</b>: si conoces JavaScript, puedes subir un
                  fichero <code className="font-mono">.js</code> que hace todo el login. Más flexible,
                  pero requiere programar.
                </p>
              </div>
            )}
          </div>

          {/* Blocked domain banner */}
          {blockedDomain && (
            <div className="rounded-md border border-red-900/60 bg-red-950/30 p-4 flex items-start gap-3">
              <Ban className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />
              <div className="text-xs text-red-200 leading-relaxed">
                <div className="font-semibold text-red-300 mb-1">
                  {blockedDomain} bloquea logins automatizados
                </div>
                Google, Microsoft, Meta y Apple detectan la automatización y te
                pedirán captcha o verificación en el móvil. Esta herramienta{" "}
                <b>no va a funcionar</b> con {blockedDomain}. Para Gemini, Gmail,
                YouTube, Outlook, etc. usa el módulo{" "}
                <Link
                  to="/browser"
                  className="underline underline-offset-2 hover:text-white"
                  onClick={() => onOpenChange(false)}
                >
                  Navegador / Playwright
                </Link>{" "}
                (inicias sesión manualmente una vez y la cookie se reutiliza).
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nombre" hint="a-z, 0-9, _ o -">
              <Input
                data-testid={TID.sessions.fieldName}
                disabled={isEdit}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="mi-sitio"
                pattern="[a-zA-Z0-9_\-]+"
                maxLength={64}
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
              />
            </Field>
            <Field label="Método de login">
              <Select value={form.method} onValueChange={(v) => set("method", v)}>
                <SelectTrigger
                  data-testid={TID.sessions.fieldMethod}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  {METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value} className="focus:bg-zinc-900">
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="URL del sitio (con sesión iniciada)">
            <Input
              data-testid={TID.sessions.fieldUrl}
              value={form.url}
              onChange={(e) => set("url", e.target.value)}
              placeholder="https://n8n.mi-dominio.com/home"
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
          </Field>

          <Field label="URL de login (opcional)">
            <Input
              data-testid={TID.sessions.fieldLoginUrl}
              value={form.login_url}
              onChange={(e) => set("login_url", e.target.value)}
              placeholder="https://n8n.mi-dominio.com/signin"
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
          </Field>

          <Field label="Selector CSS que confirma que la sesión está activa (NO es tu email)">
            <Input
              data-testid={TID.sessions.fieldLoggedInSel}
              value={form.logged_in_selector}
              onChange={(e) => set("logged_in_selector", e.target.value)}
              placeholder='.user-menu   |   [data-user-avatar]   |   #profile-btn'
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
          </Field>

          {form.method === "template" && (
            <div className="rounded-md border border-zinc-800 bg-black/40 p-4 space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                Selectores CSS del formulario de login
              </div>
              <div className="text-[11px] text-zinc-500 -mt-1">
                No es tu usuario ni tu contraseña. Es la "dirección técnica" de cada campo dentro
                de la página de login (botón derecho → Inspeccionar → Copy selector).
              </div>
              <Field label="Selector del campo de usuario">
                <Input
                  data-testid={TID.sessions.fieldUserSel}
                  value={form.user_selector}
                  onChange={(e) => set("user_selector", e.target.value)}
                  placeholder='input[type=email]   |   input[name=email]   |   #email'
                  className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                />
              </Field>
              <Field label="Selector del campo de contraseña">
                <Input
                  data-testid={TID.sessions.fieldPassSel}
                  value={form.password_selector}
                  onChange={(e) => set("password_selector", e.target.value)}
                  placeholder='input[type=password]   |   #password'
                  className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                />
              </Field>
              <Field label="Selector del botón para iniciar sesión">
                <Input
                  data-testid={TID.sessions.fieldSubmitSel}
                  value={form.submit_selector}
                  onChange={(e) => set("submit_selector", e.target.value)}
                  placeholder='button[type=submit]   |   button:has-text("Entrar")'
                  className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                />
              </Field>

              <button
                type="button"
                onClick={() => setShowAdvancedTpl((v) => !v)}
                className="w-full text-left text-[11px] text-zinc-400 hover:text-white mt-2"
              >
                Opciones avanzadas (cookies, MFA, iframe, URL de éxito) {showAdvancedTpl ? "▾" : "▸"}
              </button>
              {showAdvancedTpl && (
                <div className="space-y-3 pt-2 border-t border-zinc-800">
                  <Field label="Pre-click (banner de cookies, cambiar a pestaña email…)" hint="opcional">
                    <Input
                      data-testid={TID.sessions.fieldPreClick}
                      value={form.pre_click_selector}
                      onChange={(e) => set("pre_click_selector", e.target.value)}
                      placeholder='button#accept-cookies'
                      className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                    />
                  </Field>
                  <Field label="Iframe que contiene el formulario" hint="opcional">
                    <Input
                      data-testid={TID.sessions.fieldIframeSel}
                      value={form.iframe_selector}
                      onChange={(e) => set("iframe_selector", e.target.value)}
                      placeholder='iframe#login-frame'
                      className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                    />
                  </Field>
                  <Field label="Selector para esperar MFA (con navegador visible)" hint="opcional">
                    <Input
                      data-testid={TID.sessions.fieldMfaSel}
                      value={form.mfa_wait_selector}
                      onChange={(e) => set("mfa_wait_selector", e.target.value)}
                      placeholder='.mfa-container   |   input[name=otp]'
                      className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                    />
                  </Field>
                  <Field label="Patrón de URL que confirma el login (regex)" hint="opcional">
                    <Input
                      data-testid={TID.sessions.fieldSuccessUrl}
                      value={form.success_url_pattern}
                      onChange={(e) => set("success_url_pattern", e.target.value)}
                      placeholder='/dashboard|/home'
                      className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                    />
                  </Field>
                </div>
              )}
            </div>
          )}

          {form.method === "steps" && (
            <div className="rounded-md border border-zinc-800 bg-black/40 p-4 space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                Steps DSL — pasos declarativos de login
              </div>
              <div className="text-[11px] text-zinc-500 -mt-1 leading-relaxed">
                Un array JSON de pasos. Operaciones permitidas:{" "}
                <span className="font-mono text-zinc-300">{ALLOWED_STEP_OPS.join(", ")}</span>.{" "}
                Los valores <span className="font-mono text-emerald-400">$user</span> y{" "}
                <span className="font-mono text-emerald-400">$password</span> se sustituyen sólo en{" "}
                <span className="font-mono">value</span>. Máximo {MAX_STEPS_CLIENT} pasos.
              </div>
              <Textarea
                data-testid={TID.sessions.fieldSteps}
                value={form.steps_text}
                onChange={(e) => set("steps_text", e.target.value)}
                rows={14}
                className="bg-zinc-900 border-zinc-800 font-mono text-[12px] leading-relaxed"
                spellCheck={false}
              />
            </div>
          )}

          {editingLegacyScript && (
            <div className="rounded-md border border-amber-900/50 bg-amber-950/25 p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-200/90 leading-relaxed">
                <div className="font-semibold text-amber-300 mb-1">
                  Sesión legacy con method:"script" (deprecado)
                </div>
                Esta sesión se creó con un script JavaScript personalizado. La subida y ejecución
                de scripts está deshabilitada por seguridad. Migra la sesión a{" "}
                <span className="font-mono">Plantilla</span> o <span className="font-mono">Steps DSL</span>{" "}
                y, cuando funcione, retira el script legacy desde la tarjeta de la sesión.
              </div>
            </div>
          )}

          <div className="rounded-md border border-emerald-900/40 bg-emerald-950/20 p-4 space-y-3">
            <div className="text-[11px] text-emerald-300 flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5" />
              Credenciales cifradas (AES-256-GCM)
            </div>
            <Field
              label="Usuario / email"
              hint={isEdit && editing?.user_masked ? `guardado: ${editing.user_masked}` : undefined}
            >
              <Input
                data-testid={TID.sessions.fieldUser}
                value={form.user}
                onChange={(e) => set("user", e.target.value)}
                placeholder={isEdit ? "Dejar vacío para conservar" : "usuario@dominio.com"}
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                autoComplete="off"
              />
            </Field>
            <Field
              label="Contraseña"
              hint={isEdit && editing?.has_credentials ? "guardada" : undefined}
            >
              <Input
                data-testid={TID.sessions.fieldPass}
                type="password"
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                placeholder={isEdit ? "Dejar vacío para conservar" : "••••••••"}
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                autoComplete="new-password"
              />
            </Field>
          </div>

          <div className="rounded-md border border-amber-900/40 bg-amber-950/20 p-3 flex items-start gap-2 text-[11px] text-amber-200/80">
            <ShieldAlert className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
            No uses esta herramienta con Google, Microsoft, Meta o Apple —
            bloquean logins automatizados con captcha / 2FA. Para Gemini usa el
            flujo de <span className="font-semibold">Navegador / Playwright</span>{" "}
            (storage_state).
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid={TID.sessions.submit}
              disabled={saving || Boolean(blockedDomain)}
              className="bg-white text-black hover:bg-zinc-200 disabled:opacity-40"
            >
              {saving ? "Guardando…" : blockedDomain ? "Bloqueado para " + blockedDomain : isEdit ? "Guardar cambios" : "Crear sesión"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-zinc-300 flex items-center gap-2">
        {label}
        {hint && <span className="text-[11px] font-mono text-emerald-400">{hint}</span>}
      </Label>
      {children}
    </div>
  );
}
